import { useMemo, type CSSProperties } from "react";
import type { GeneratedStatus, InstanceDetail, InstanceGroupConsolidated, InstanceGroupSummary } from "@keydials/api-client";
import { Icon } from "../components/Icon";
import { DialView } from "../components/DialView";
import type { DialVM, VSection, VSegment } from "../dialModel";
import { RAG_COLOUR, type Rag } from "../lib/ui";
import { ScoreBadge } from "./groupUi";

const NONE = "#94a3b8"; // --st-none, for a Part with no status anywhere
const RANK: Record<Rag, number> = { Green: 0, Amber: 1, Red: 2 };

/** WorstOf roll-up of a Part's per-segment bands (Red beats Amber beats Green); null when none are set. */
function worstBand(bands: (Rag | null)[]): Rag | null {
  let worst: Rag | null = null;
  for (const b of bands) {
    if (b && (worst === null || RANK[b] > RANK[worst])) worst = b;
  }
  return worst;
}

/**
 * A presentation-only DialVM for a uniform group: each member dial becomes an outer section, and each of
 * the shared definition's sections (e.g. "Part A") becomes one inner segment, coloured by a WorstOf
 * roll-up of that section's segments for that member. Synthetic — only the fields {@link DialView} reads
 * are populated (so a couple of casts stand in for a real instance/status).
 */
export function buildGroupDialVM(con: InstanceGroupConsolidated): DialVM {
  // The shared definition's sections (Parts), in first-seen (definition) order.
  const parts: { key: string; label: string }[] = [];
  const seen = new Set<string>();
  for (const row of con.rows) {
    if (!seen.has(row.sectionKey)) {
      seen.add(row.sectionKey);
      parts.push({ key: row.sectionKey, label: row.sectionLabel });
    }
  }

  const sections: VSection[] = con.members.map((member, mi) => {
    // Distinguishing pod label (org name, e.g. "sizewell pod 1"); falls back to the dial name or ordinal.
    const podLabel = member.organisationName?.trim() || member.name || `Dial ${mi + 1}`;
    const segments: VSegment[] = parts.map((part) => {
      const band = worstBand(
        con.rows.filter((r) => r.sectionKey === part.key).map((r) => r.cells[mi]?.band ?? null),
      );
      const colour = band ? RAG_COLOUR[band] : NONE;
      return {
        key: `${member.instanceId}:${part.key}`,
        label: part.label.replace(/^Part\s+/i, ""), // "A" from "Part A" for the tight inner ring
        name: `${podLabel} · ${part.label}`,
        items: [],
        itemActive: false, // synthetic band segment — the tooltip reads its status, not an item tally
        colour,
        // Minimal synthetic status so the hover tooltip reads the band ("Red"/"Amber"/"Green").
        status: band
          ? ({ key: band, name: band, colour, order: 0, isTerminal: band === "Green" } as unknown as GeneratedStatus)
          : undefined,
        palette: [],
        done: band === "Green" ? 1 : 0,
        total: 1,
        overdue: false,
        band: band ?? undefined,
      };
    });
    return {
      key: member.instanceId,
      label: podLabel,
      segments,
      done: segments.reduce((n, s) => n + s.done, 0),
      total: segments.length,
    };
  });

  const allSegs = sections.flatMap((s) => s.segments);
  const greens = allSegs.filter((s) => s.band === "Green").length;

  return {
    detail: { id: con.id, name: con.name, centralImageUrl: null } as unknown as InstanceDetail,
    simple: true,
    sections,
    dimensions: [],
    completion: allSegs.length ? greens / allSegs.length : 0,
    done: greens,
    total: allSegs.length,
    attention: 0,
    itemCount: allSegs.length,
    sectionCount: sections.length,
    isGroup: true,
  };
}

/** The group rendered as a consolidated Key Dial: a slice per member, each Part rolled up to a band. */
export function GroupDial({ data }: { data: InstanceGroupConsolidated }) {
  const vm = useMemo(() => buildGroupDialVM(data), [data]);

  if (!data.uniform) {
    return (
      <div className="kd-card" style={notice}>
        The group ring needs every dial on the same definition. “{data.name}” mixes definitions — use{" "}
        <strong>Portfolio</strong> or <strong>By area</strong> instead.
      </div>
    );
  }

  return (
    <div className="kd-card" style={{ padding: 24 }}>
      <div style={{ width: "min(560px, 100%)", aspectRatio: "1 / 1", margin: "0 auto" }}>
        <DialView vm={vm} progress interactive />
      </div>
      <div style={legendRow}>
        {data.members.map((m, i) => (
          <span key={m.instanceId} style={legendItem}>
            <span style={{ fontWeight: 800, color: "var(--text)" }}>{i + 1}</span>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 260 }}>
              {m.organisationName || m.name}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

const notice: CSSProperties = { padding: 24, color: "var(--text-muted)", fontSize: 14, lineHeight: 1.6 };
const legendRow: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "6px 18px",
  justifyContent: "center",
  marginTop: 16,
  fontSize: 12.5,
  color: "var(--text-muted)",
};
const legendItem: CSSProperties = { display: "inline-flex", gap: 7, alignItems: "baseline", minWidth: 0 };

/** A My Dials card for a group: the consolidated ring (when uniform) with the group's name + health. */
export function GroupDialCard({
  summary,
  consolidated,
  onOpen,
}: {
  summary: InstanceGroupSummary;
  consolidated: InstanceGroupConsolidated | null;
  onOpen: () => void;
}) {
  const vm = useMemo(() => (consolidated?.uniform ? buildGroupDialVM(consolidated) : null), [consolidated]);
  return (
    <button onClick={onOpen} className="kd-card kd-card-hover kd-focusable" style={cardStyle}>
      <div style={{ display: "flex", gap: 18, alignItems: "center" }}>
        <div style={{ width: 104, height: 104, flex: "none", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {vm ? <DialView vm={vm} interactive={false} showLabels={false} /> : <Icon name="layers" size={40} style={{ color: "var(--text-faint)" }} />}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <span style={tag}><Icon name="layers" size={12} /> Group</span>
          <div className="display" style={{ fontSize: 19, fontWeight: 700, color: "var(--text)", lineHeight: 1.2, marginTop: 5 }}>{summary.name}</div>
          <div style={{ fontSize: 13, color: "var(--text-faint)", marginTop: 3, fontWeight: 500 }}>
            {summary.memberCount} dial{summary.memberCount === 1 ? "" : "s"} · {summary.rollupStrategy}
          </div>
          <div style={{ marginTop: 11 }}>
            <ScoreBadge score={summary.score} classification={summary.classification} />
          </div>
        </div>
      </div>
    </button>
  );
}

const cardStyle: CSSProperties = {
  padding: "var(--pad-card)",
  textAlign: "left",
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  gap: "var(--gap-stack)",
  font: "inherit",
  color: "inherit",
  width: "100%",
};
const tag: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: "var(--text-faint)",
};
